import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { getSocket } from "../api/socket";
import client from "../api/client";

interface WatchVideo {
  id: string;
  title: string;
  description: string | null;
  youtubeUrl: string | null;
  videoId: string | null;
  fileUrl: string | null;
  sortOrder: number;
  createdAt: string;
}

interface ChatMsg {
  id: string;
  userId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  content: string;
  type: "message" | "solve" | "first_blood" | "join" | "system";
  timestamp: Date | string;
}

function getYouTubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`;
}

function getYouTubeThumb(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}

function formatTime(date: Date | string) {
  const d = new Date(date);
  return d.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit" });
}

function VideoPlayer({ video }: { video: WatchVideo }) {
  if (video.videoId) {
    return (
      <div className="aspect-video overflow-hidden rounded-xl border border-ds-border bg-black">
        <iframe
          src={getYouTubeEmbedUrl(video.videoId)}
          className="h-full w-full"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title={video.title}
        />
      </div>
    );
  }
  if (video.fileUrl) {
    return (
      <div className="aspect-video overflow-hidden rounded-xl border border-ds-border bg-black">
        <video
          src={video.fileUrl}
          controls
          className="h-full w-full object-contain"
        />
      </div>
    );
  }
  return (
    <div className="flex aspect-video items-center justify-center rounded-xl border border-ds-border bg-ds-surface">
      <span className="text-sm text-ds-text-muted">Video mavjud emas</span>
    </div>
  );
}

export default function WatchPage() {
  const { user } = useAuth();
  const [videos, setVideos] = useState<WatchVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState<WatchVideo | null>(null);

  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [onlineCount, setOnlineCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    client.get("/watch/videos")
      .then((res) => {
        const list = res.data?.data ?? [];
        setVideos(list);
        if (list.length > 0) setSelectedVideo(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    const socket = getSocket();

    client.get("/chat/messages?limit=50").then((res) => {
      if (res.data?.success) {
        setMessages(res.data.data.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })));
      }
    }).catch(() => {});

    socket.emit("chat:join", { username: user.username, displayName: user.displayName });

    const onMessage = (msg: ChatMsg) => {
      setMessages((prev) => [...prev, { ...msg, timestamp: new Date(msg.timestamp) }]);
    };
    const onSystem = (evt: { type: string; message: string; timestamp: string }) => {
      setMessages((prev) => [
        ...prev,
        { id: `sys-${Date.now()}`, userId: "system", username: "SYSTEM", displayName: null, avatarUrl: null, content: evt.message, type: evt.type as any, timestamp: new Date(evt.timestamp) },
      ]);
    };
    const onOnline = (data: { count: number }) => setOnlineCount(data.count);
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    socket.on("chat:message", onMessage);
    socket.on("chat:system", onSystem);
    socket.on("chat:online", onOnline);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    if (socket.connected) setConnected(true);

    return () => {
      socket.off("chat:message", onMessage);
      socket.off("chat:system", onSystem);
      socket.off("chat:online", onOnline);
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = useCallback(() => {
    if (!input.trim() || !user) return;
    const socket = getSocket();
    socket.emit("chat:send", { content: input.trim() });
    setInput("");
  }, [input, user]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ds-border border-t-ds-accent" />
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-ds-bg">
      {/* Main content */}
      <div className="flex flex-1 flex-col">
        {/* Header */}
        <div className="border-b border-ds-border px-6 py-4">
          <h1 className="text-xl font-semibold text-ds-text">Watch</h1>
          <p className="mt-1 text-sm text-ds-text-muted">
            Trening materiallari va video darslar
          </p>
        </div>

        {/* Video player + list */}
        <div className="flex flex-1 flex-col lg:flex-row">
          {/* Player area */}
          <div className="flex-1 p-4">
            {selectedVideo ? (
              <VideoPlayer video={selectedVideo} />
            ) : (
              <div className="flex aspect-video items-center justify-center rounded-xl border border-ds-border bg-ds-surface">
                <span className="text-sm text-ds-text-muted">Video tanlang</span>
              </div>
            )}

            {selectedVideo && (
              <div className="mt-4">
                <h2 className="text-lg font-medium text-ds-text">{selectedVideo.title}</h2>
                {selectedVideo.description && (
                  <p className="mt-2 text-sm text-ds-text-muted">{selectedVideo.description}</p>
                )}
              </div>
            )}
          </div>

          {/* Video list */}
          <div className="w-full border-t border-ds-border lg:w-72 lg:border-t-0 lg:border-l">
            <div className="border-b border-ds-border px-4 py-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-ds-text-muted">
                Videos [{videos.length}]
              </h3>
            </div>
            <div className="max-h-[300px] overflow-y-auto lg:max-h-none">
              {videos.length === 0 ? (
                <div className="p-4 text-center text-sm text-ds-text-muted">
                  Hozircha video yo'q
                </div>
              ) : (
                videos.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVideo(v)}
                    className={`flex w-full items-start gap-3 border-b border-ds-border/50 px-4 py-3 text-left transition-colors ${
                      selectedVideo?.id === v.id
                        ? "bg-ds-accent/10"
                        : "hover:bg-ds-surface/50"
                    }`}
                  >
                    {v.videoId ? (
                      <img
                        src={getYouTubeThumb(v.videoId)}
                        alt=""
                        className="h-14 w-20 shrink-0 rounded object-cover"
                      />
                    ) : v.fileUrl ? (
                      <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded bg-ds-surface">
                        <svg className="h-5 w-5 text-ds-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                        </svg>
                      </div>
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ds-text truncate">{v.title}</p>
                      {v.description && (
                        <p className="mt-0.5 text-xs text-ds-text-muted truncate">{v.description}</p>
                      )}
                      <span className="mt-1 inline-block rounded bg-ds-surface px-1.5 py-0.5 text-[10px] text-ds-text-muted">
                        {v.videoId ? "YouTube" : "Local"}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chat sidebar */}
      <div className="hidden w-72 flex-col border-l border-ds-border lg:flex">
        <div className="flex items-center justify-between border-b border-ds-border px-4 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-ds-text-muted">
            Chat
          </h3>
          <div className="flex items-center gap-1.5">
            <div className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-ds-success animate-pulse" : "bg-ds-danger"}`} />
            <span className="font-mono text-[10px] text-ds-text-muted">
              {onlineCount} online
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
          {messages.map((msg) => (
            <div key={msg.id} className="flex items-start gap-2 py-1 px-1 rounded text-xs">
              <span className="font-mono text-[10px] text-ds-text-muted shrink-0 mt-0.5">
                {formatTime(msg.timestamp)}
              </span>
              {msg.type === "message" ? (
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-ds-accent">{msg.displayName || msg.username}: </span>
                  <span className="text-ds-text break-words">{msg.content}</span>
                </div>
              ) : msg.type === "solve" || msg.type === "first_blood" ? (
                <div className="min-w-0 flex-1">
                  <span className={`font-bold ${msg.type === "first_blood" ? "text-ds-danger" : "text-ds-success"}`}>
                    {msg.type === "first_blood" ? "FIRST BLOOD" : "SOLVED"}
                  </span>
                  <span className="text-ds-text-dim"> {msg.username}</span>
                </div>
              ) : (
                <span className="text-ds-text-muted italic">{msg.content}</span>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-ds-border px-3 py-2">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={500}
              placeholder="Xabar yozing..."
              className="flex-1 bg-ds-surface rounded px-3 py-1.5 text-xs text-ds-text placeholder-ds-text-muted outline-none focus:ring-1 focus:ring-ds-accent"
              autoComplete="off"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim()}
              className="shrink-0 rounded bg-ds-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-ds-accent/90 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
